import { ReactElement, useState, useMemo, useCallback } from 'react';

import {
  useTypePartnerCompany,
  useCompleteInitialConfig,
  useRequest,
  useToggle,
} from '~hooks/index';
import { Nullable } from '~globals/types';
import { getStatistics } from '~services/home';
import { HomeStatistics } from '~services/home/types';
import {
  MainAccountDashboard,
  RoutesStatiticsWidget,
  FleetStatisticsWidget,
  DashboardOrdersReport,
  AlertOrders,
  DailyStatitics,
  DeliveryStates,
  DialogDriverCompanyInvite,
  DialogInvitationPartnerCompany,
  DialogAssociatePartnerCompany,
} from '~components/index';

import {
  DashboardLoadingWrapper,
  DashboardCompleteInitialConfigWrapper,
  DashboardWidgetsWrapper,
} from '../../Wrappers';

import {
  DashboardVariantV1Container,
  DashboardVariantV1GridStack,
} from './styles';

const DashboardVariantV1 = (): ReactElement => {
  const { isCarrier } = useTypePartnerCompany();
  const { setCompletedConfig, handleOpenMenuFirstStepOnCompleted } =
    useCompleteInitialConfig();

  const [statistics, setStatistics] = useState<Nullable<HomeStatistics>>(null);
  const [, loadingStatics, , reloadStatistics] = useRequest(
    {
      request: getStatistics,
      payload: null,
      withPostSuccess: (response) => {
        const currentStatistics = response.data?.data as HomeStatistics;
        setStatistics(currentStatistics);

        handleOpenMenuFirstStepOnCompleted();
      },
    },
    [],
  );

  const vehiclesCount = useMemo(
    () => statistics?.quantityVehicles ?? 0,
    [statistics?.quantityVehicles],
  );
  const warehousesCount = useMemo(
    () => statistics?.quantityWarehouses ?? 0,
    [statistics?.quantityWarehouses],
  );

  const onCompleteInitialConfig = useCallback(() => {
    setCompletedConfig(true);
    reloadStatistics(null);
  }, [setCompletedConfig, reloadStatistics]);

  const [openDialogInviteDriver, toogleOpenDialogInviteDriver] = useToggle();

  const [
    openDialogInvitationPartnerCompany,
    toogleOpenDialogInvitationPartnerCompany,
  ] = useToggle(false);

  const [
    openDialogAssociatePartnerCompany,
    toogleOpenDialogAssociatePartnerCompany,
  ] = useToggle(false);

  return (
    <DashboardLoadingWrapper loading={loadingStatics}>
      <DashboardCompleteInitialConfigWrapper
        vehiclesCount={vehiclesCount}
        warehousesCount={warehousesCount}
        onComplete={onCompleteInitialConfig}
      >
        <DashboardWidgetsWrapper>
          <DashboardVariantV1Container>
            <DashboardVariantV1GridStack
              direction="row"
              spacing={2}
              gridAutoRows="max-content"
            >
              <MainAccountDashboard
                vehiclesCount={vehiclesCount}
                depositsCount={warehousesCount}
                driversCount={statistics?.drivers.length ?? 0}
                users={statistics?.users ?? []}
                partnerCompanies={statistics?.partnerCompanies ?? []}
                handleOpenDialogInviteDriver={() =>
                  toogleOpenDialogInviteDriver(true)
                }
                handleOpenDialogInvitePartnerCompany={() =>
                  toogleOpenDialogAssociatePartnerCompany(true)
                }
              />

              {isCarrier && (
                <DashboardVariantV1GridStack
                  spacing={2}
                  direction="column"
                  gridAutoColumns="1fr"
                >
                  <RoutesStatiticsWidget
                    total={statistics?.totalRoutes ?? 0}
                    pending={statistics?.totalRoutesPending ?? 0}
                    inProgress={statistics?.totalRoutesInProgress ?? 0}
                    finish={statistics?.totalRoutesCompleted ?? 0}
                    size="medium"
                  />

                  <FleetStatisticsWidget
                    avgLoadByVehicle={statistics?.avgLoadByVehicle ?? 0}
                    estimatedFuelConsumed={
                      statistics?.estimatedFuelConsution ?? 0
                    }
                    estimatedCo2Emission={statistics?.totalCo2Emmision ?? 0}
                  />
                </DashboardVariantV1GridStack>
              )}

              <DashboardOrdersReport
                previousYearVisitCompleted={
                  statistics?.previousYearVisitTotalCount ?? []
                }
                currentYearVisitCompleted={
                  statistics?.currentYearVisitTotalCount ?? []
                }
              />
            </DashboardVariantV1GridStack>

            <DashboardVariantV1GridStack
              direction="row"
              spacing={2}
              gridAutoRows="max-content"
            >
              {!statistics?.totalItemExpired &&
                !statistics?.totalItemExpiredToday && <AlertOrders />}

              {!!statistics?.totalItemExpiredToday && (
                <AlertOrders
                  totalItemExpiredToday={statistics.totalItemExpiredToday}
                />
              )}

              {!!statistics?.totalItemExpired && (
                <AlertOrders totalItemExpired={statistics?.totalItemExpired} />
              )}

              {isCarrier && (
                <DailyStatitics
                  stops={statistics?.quantityDestiny ?? 0}
                  distance={statistics?.totalDistance ?? 0}
                  time={statistics?.totalTime ?? 0}
                />
              )}

              <DeliveryStates
                pending={statistics?.totalPendingItems ?? 0}
                onAgenda={statistics?.totalBookedItems ?? 0}
                inProgress={statistics?.totalInProgressItems ?? 0}
                unComplete={statistics?.totalRejectedItems ?? 0}
                complete={statistics?.totalCompleteItems ?? 0}
                withoutVisiting={statistics?.totalWithoutVisitItems ?? 0}
              />
            </DashboardVariantV1GridStack>

            <DialogDriverCompanyInvite
              open={openDialogInviteDriver}
              onClose={() => toogleOpenDialogInviteDriver(false)}
            />

            <DialogInvitationPartnerCompany
              open={openDialogInvitationPartnerCompany}
              onClose={() => toogleOpenDialogInvitationPartnerCompany(false)}
            />

            <DialogAssociatePartnerCompany
              open={openDialogAssociatePartnerCompany}
              onClose={() => toogleOpenDialogAssociatePartnerCompany(false)}
              onOpenDialogInvitationCompany={() =>
                toogleOpenDialogInvitationPartnerCompany(true)
              }
            />
          </DashboardVariantV1Container>
        </DashboardWidgetsWrapper>
      </DashboardCompleteInitialConfigWrapper>
    </DashboardLoadingWrapper>
  );
};

export default DashboardVariantV1;
